import { useId, useState } from 'react';
import { useRouter } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  Field,
  Input,
  SearchSelect,
  Select,
  SettingsRow,
  SettingsSection,
  Spinner,
  Switch,
  Textarea,
} from '@wundo/ui';
import { fetchCurrentUser } from '@wundo/client/auth.ts';
import type {
  OrganisationFeature,
  OrganisationFeatures,
} from '@wundo/shared/organisationFeature.ts';
import type { Cohort } from '@/types/cohorts.ts';
import { fetchCohort } from '@/lib/cohorts.ts';
import {
  ORGANISATION_FEATURE_DEFINITIONS,
  ORGANISATION_TYPES,
  organisationFeatureLabel,
  searchOrganisationCohorts,
  type OrganisationDetail,
  type OrganisationType,
} from '@/lib/organisations.ts';
import { formatDateTime } from '@/utils/dateUtils.ts';
import { CalendarProxyStatusPanel } from './CalendarProxyStatusPanel.tsx';
import {
  useOrganisationForm,
  type OrganisationFormState,
  type OrganisationPrefill,
} from './useOrganisationForm.ts';

export interface OrganisationFormProps {
  /** The organisation being edited. Absent when one is being created. */
  organisationId?: string;
  /** Starting values for a new organisation; ignored when editing. */
  prefill?: OrganisationPrefill;
}

/** The one form behind both `/organisations/create` and `/organisations/$id/edit`. */
export function OrganisationForm({ organisationId, prefill }: OrganisationFormProps) {
  const router = useRouter();
  const fieldId = useId();
  const isEditing = organisationId !== undefined;

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
  });
  const isSuperAdmin = currentUser?.role === 'superadmin';

  const { isPending, isError, loaded, form, update, setFeature, save, saved, error } =
    useOrganisationForm(organisationId, prefill);

  if (isEditing && isPending) {
    return (
      <Card>
        <div className='flex items-center gap-2 py-3 text-sm font-semibold text-ink-muted'>
          <Spinner />
          Loading…
        </div>
      </Card>
    );
  }

  if (isEditing && isError) {
    return (
      <Card>
        <p role='alert' className='text-sm font-semibold text-danger'>
          Failed to load organisation
        </p>
      </Card>
    );
  }

  return (
    <form
      className='flex flex-col gap-5'
      onSubmit={(event) => {
        event.preventDefault();
        save.mutate();
      }}
    >
      {saved && (
        <p
          role='status'
          className='rounded-md bg-accent-tint px-4 py-3 text-sm font-semibold text-success'
        >
          Organisation saved successfully!
        </p>
      )}
      {error !== '' && (
        <p
          role='alert'
          className='rounded-md bg-danger-fill px-4 py-3 text-sm font-semibold text-danger'
        >
          {error}
        </p>
      )}

      <Card className='flex flex-col gap-5'>
        <Field htmlFor={`${fieldId}-name`} label='Name'>
          <Input
            id={`${fieldId}-name`}
            value={form.name}
            required
            onChange={(event) => update({ name: event.target.value })}
          />
        </Field>

        <Field htmlFor={`${fieldId}-type`} label='Type'>
          <Select
            id={`${fieldId}-type`}
            options={ORGANISATION_TYPES}
            value={form.type}
            wrapperClassName='w-full'
            className='w-full'
            onChange={(event) => update({ type: event.target.value as OrganisationType })}
          />
        </Field>

        <Field
          htmlFor={`${fieldId}-msteams`}
          label='Microsoft Teams Tenant ID'
          description='The Microsoft Teams Tenant ID is used to integrate with Microsoft Teams services.'
        >
          <Input
            id={`${fieldId}-msteams`}
            value={form.msteams_tenant_id}
            placeholder='Enter Microsoft Teams Tenant ID (optional)'
            onChange={(event) => update({ msteams_tenant_id: event.target.value })}
          />
        </Field>

        <OrganisationDomainFields
          fieldId={fieldId}
          form={form}
          onChange={update}
          loaded={loaded}
        />

        <Field
          htmlFor={`${fieldId}-calendar-proxy`}
          label='Calendar Connector URL'
          description="The customer's own Wundo Calendar Connector, such as https://acme-agendaproxy.azurewebsites.net. Set, people connect their Microsoft calendar through it and Wundo only reads what it lets through. Signing in is not affected."
        >
          <Input
            id={`${fieldId}-calendar-proxy`}
            value={form.calendar_proxy_url ?? ''}
            placeholder='https://… (optional)'
            onChange={(event) =>
              update({
                calendar_proxy_url:
                  event.target.value.trim() === '' ? null : event.target.value,
              })
            }
          />
        </Field>

        {organisationId !== undefined && loaded?.calendar_proxy_url ? (
          <CalendarProxyStatusPanel organisationId={organisationId} />
        ) : null}

        {organisationId !== undefined && isSuperAdmin && (
          <DefaultCohortField
            id={`${fieldId}-cohort`}
            organisationId={organisationId}
            storedCohortId={loaded?.default_cohort_id ?? null}
            value={form.default_cohort_id}
            onChange={(cohortId) => update({ default_cohort_id: cohortId })}
          />
        )}
      </Card>

      {isSuperAdmin && ORGANISATION_FEATURE_DEFINITIONS.length > 0 && (
        <OrganisationFeaturesSection features={form.features} onChange={setFeature} />
      )}

      <div className='flex justify-end gap-3'>
        <Button variant='quiet' onClick={() => router.history.back()}>
          Cancel
        </Button>
        <Button type='submit' loading={save.isPending}>
          {save.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </form>
  );
}

interface OrganisationDomainFieldsProps {
  fieldId: string;
  form: OrganisationFormState;
  onChange: (patch: Partial<OrganisationFormState>) => void;
  loaded: OrganisationDetail | undefined;
}

function OrganisationDomainFields({
  fieldId,
  form,
  onChange,
  loaded,
}: OrganisationDomainFieldsProps) {
  /*
   * Read off the saved record, not the form: the list is Microsoft's once a
   * fetch for the stored domain succeeded, and a domain typed but not yet
   * saved has fetched nothing.
   */
  const domainsFromMicrosoft =
    Boolean(loaded?.primary_domain) && Boolean(loaded?.internal_domains_synced_at);
  const domainsWarning = loaded?.primary_domain ? loaded.internal_domains_warning : null;

  return (
    <>
      <Field
        htmlFor={`${fieldId}-primary-domain`}
        label='Domain'
        description="The company's own email domain, such as acme.com. When it belongs to a Microsoft organisation, Wundo fetches all of that organisation's domains into Internal Domains and keeps them up to date every week."
      >
        <Input
          id={`${fieldId}-primary-domain`}
          value={form.primary_domain ?? ''}
          placeholder='acme.com (optional)'
          onChange={(event) =>
            onChange({
              primary_domain:
                event.target.value.trim() === '' ? null : event.target.value,
            })
          }
        />
      </Field>

      {domainsWarning ? (
        <p
          role='alert'
          className='rounded-md bg-danger-fill px-4 py-3 text-sm font-semibold text-danger'
        >
          {domainsWarning}
        </p>
      ) : null}

      <Field
        htmlFor={`${fieldId}-domains`}
        label='Internal Domains'
        description={
          domainsFromMicrosoft
            ? `Fetched from Microsoft, last updated ${formatDateTime(loaded?.internal_domains_synced_at)}. Everyone on these domains counts as a colleague. Change the domain above to change this list.`
            : 'One domain per line. Everyone on these domains counts as a colleague, which is what tells a 1:1 or a team meeting apart from a meeting with outsiders. A company often owns several. Leave empty to fall back to the domain people sign in with.'
        }
      >
        <Textarea
          id={`${fieldId}-domains`}
          rows={4}
          sizing='manual'
          className='font-mono text-sm'
          placeholder={'acme.com\nacme-group.be'}
          value={form.internal_domains.join('\n')}
          readOnly={domainsFromMicrosoft}
          onChange={(event) =>
            onChange({ internal_domains: linesToList(event.target.value) })
          }
        />
      </Field>
    </>
  );
}

/*
 * One entry per line, which is how someone pastes a list of domains. Blank
 * lines and stray spaces are dropped rather than stored, so an empty box means
 * an empty list instead of a list holding nothing.
 */
function linesToList(value: string): string[] {
  return value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
}

interface DefaultCohortFieldProps {
  id: string;
  organisationId: string;
  /** The cohort stored on the organisation, before any edit. */
  storedCohortId: string | null;
  value: string | null;
  onChange: (cohortId: string | null) => void;
}

function DefaultCohortField({
  id,
  organisationId,
  storedCohortId,
  value,
  onChange,
}: DefaultCohortFieldProps) {
  /*
   * The cohort behind the stored id, so the picker shows a title rather than a
   * uuid. Only the stored one is looked up — once somebody picks a different
   * one, the record is already in hand.
   */
  const { data: storedCohort } = useQuery({
    queryKey: ['cohort', storedCohortId],
    queryFn: () => fetchCohort(storedCohortId as string),
    enabled: storedCohortId !== null,
  });
  const [pickedCohort, setPickedCohort] = useState<Cohort | null>(null);
  const defaultCohort =
    [pickedCohort, storedCohort].find(
      (cohort) => cohort != null && cohort.id === value,
    ) ?? null;

  return (
    <Field
      htmlFor={id}
      label='Default Cohort'
      description='New users who install Wundo via Microsoft Teams will automatically be assigned to this cohort.'
    >
      <SearchSelect
        id={id}
        clearable
        placeholder='Search for a cohort...'
        value={defaultCohort}
        loadOptions={(query, signal) =>
          searchOrganisationCohorts(organisationId, query, signal)
        }
        getKey={(cohort: Cohort) => cohort.id}
        getLabel={(cohort: Cohort) => cohort.title}
        onChange={(cohort) => {
          setPickedCohort(cohort);
          onChange(cohort?.id ?? null);
        }}
      />
    </Field>
  );
}

function OrganisationFeaturesSection({
  features,
  onChange,
}: {
  features: OrganisationFeatures;
  onChange: (key: OrganisationFeature, value: boolean) => void;
}) {
  return (
    <SettingsSection title='Features'>
      {ORGANISATION_FEATURE_DEFINITIONS.map((feature) => (
        <SettingsRow
          key={feature.key}
          label={organisationFeatureLabel(feature.key)}
          description={feature.description}
        >
          <Switch
            aria-label={organisationFeatureLabel(feature.key)}
            checked={features[feature.key] ?? feature.default}
            onCheckedChange={(checked) => onChange(feature.key, checked)}
          />
        </SettingsRow>
      ))}
    </SettingsSection>
  );
}
